import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AuthorityFilter,
    DashboardService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionService,
    NoticesService,
    PersonsOrganisationDto,
    PreferencesService,
    hasAnyPermission
} from '@flyfreely-portal-ui/flyfreely';
import {
    CurrentOrganisation,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { Angulartics2 } from 'angulartics2';
import { MissionDialoguesService } from 'libs/missions/src/lib/mission-dialogues.service';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MissionViewService } from '../mission-view.service';

interface MissionViewSidebarPreferences {
    viewMode: 'MAP' | 'LIST' | 'BOTH';
}

@Component({
    selector: 'flyfreely-portal-ui-mission-view-sidebar',
    templateUrl: './mission-view-sidebar.component.html',
    styleUrls: ['./mission-view-sidebar.component.scss'],
    animations: [collapseOnLeaveAnimation(), expandOnEnterAnimation()]
})
export class MissionViewSidebarComponent implements OnInit {
    organisation: PersonsOrganisationDto;

    isLoading = false;

    canUseCalendar = true;
    canUseLotams = false;
    canListMissions = true;
    hasFlightLogging = true;
    canReport = true;
    canAddMission = true;
    canViewOrganisation = false;

    showOptions = true;
    showControls = true;

    numberOfUnacknowledgedLotams: number;

    missionViewMode: 'MAP' | 'LIST' | 'BOTH' = 'BOTH';

    missionAuthorityFilter: AuthorityFilter;

    missionAuthorityFilters$: Observable<
        { name: string; children: AuthorityFilter[] }[]
    >;

    private ngUnsubscribe$ = new Subject<void>();
    private preferencesLoaded = false;

    constructor(
        private missionDialoguesService: MissionDialoguesService,
        private missionViewService: MissionViewService,
        private sharedService: WorkspaceStateService,
        private router: Router,
        private missionService: MissionService,
        private noticesService: NoticesService,
        public dashboardService: DashboardService,
        private activatedRoute: ActivatedRoute,
        private logging: FlyFreelyLoggingService,
        private preferencesService: PreferencesService,
        private angulartics2: Angulartics2
    ) {}

    ngOnInit(): void {
        this.sharedService.currentOrganisation$
            .pipe(
                filter<CurrentOrganisation>(
                    o => o.type === 'organisation_loaded'
                )
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.canViewOrganisation = hasAnyPermission(
                    organisation.organisation,
                    PersonsOrganisationDto.Permissions.ORGANISATION_VIEW
                );
                this.loadUserPreferences();
                if (this.canUseLotams && this.canViewOrganisation) {
                    this.findNumberOfUnacknowledgedNotices();
                }
            });

        this.missionViewService.permissionsUpdated$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(updated => {
                if (updated) {
                    this.canUseCalendar =
                        this.missionViewService.canUseCalendar;
                    this.canUseLotams = this.missionViewService.canUseLotams;
                    this.canListMissions =
                        this.missionViewService.canListMissions;
                    this.hasFlightLogging =
                        this.missionViewService.hasFlightLogging;
                    this.canReport = this.missionViewService.canReport;
                    this.canAddMission = this.missionViewService.canAddMission;
                }
                if (this.canUseLotams && this.canViewOrganisation) {
                    this.findNumberOfUnacknowledgedNotices();
                }
            });

        this.noticesService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                if (this.canUseLotams && this.canViewOrganisation) {
                    this.findNumberOfUnacknowledgedNotices();
                }
            });
        this.missionViewService.isMissionDataLoading$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(isLoading => {
                this.isLoading = isLoading;
            });
        this.missionViewService.viewMode$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(mode => {
                this.missionViewMode = mode;
                this.saveUserPreferences();
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    loadUserPreferences() {
        if (!this.organisation) {
            return;
        }

        this.preferencesService
            .findPreferences<MissionViewSidebarPreferences>(
                'missionViewSidebar',
                this.organisation.id
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: preferences => {
                    if (preferences && preferences.viewMode) {
                        this.missionViewMode = preferences.viewMode;
                        this.missionViewService.setViewMode(
                            this.missionViewMode
                        );
                    }
                    this.preferencesLoaded = true;
                },
                error: () => {
                    this.preferencesLoaded = true;
                }
            });
    }

    saveUserPreferences() {
        if (!this.organisation || !this.preferencesLoaded) {
            return;
        }

        const preferences: MissionViewSidebarPreferences = {
            viewMode: this.missionViewMode
        };

        this.preferencesService
            .updatePreferences(
                'missionViewSidebar',
                this.organisation.id,
                preferences
            )
            .subscribe();
    }

    findNumberOfUnacknowledgedNotices() {
        this.noticesService
            .countNoticesRequiringAcknowledgement(this.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: count => (this.numberOfUnacknowledgedLotams = count),
                error: (error: FlyFreelyError) => this.logging.error(error)
            });
    }

    newMission() {
        this.missionDialoguesService.showMissionEditor(
            this.missionService.newMission(this.organisation),
            this.organisation
        );
    }

    showMissionReportsDialogue() {
        this.missionViewService.showMissionReportsDialogue();
    }

    showFlightLogsDialogue() {
        this.router.navigate(['flightlogs'], {
            queryParamsHandling: 'preserve',
            relativeTo: this.activatedRoute.parent
        });
    }

    showMissionCalendar() {
        this.missionViewService.showMissionCalendar();
    }

    updateMissionViewMode() {
        this.angulartics2.eventTrack.next({
            action: 'update-mission-view-mode',
            properties: {
                category: 'mission-view',
                label: this.missionViewMode
            }
        });
        this.missionViewService.setViewMode(this.missionViewMode);
        this.saveUserPreferences();
    }

    showOrganisationLotams() {
        this.missionViewService.showOrganisationLotams();
    }
}
